import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, BookOpen, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ParameterInput } from '@/components/parameter/ParameterInput';
import { Parameter } from '@shared/types';
import { type CheatsheetEntry } from './cheatsheetData';
import { AnnotatedSnippet } from './AnnotatedSnippet';

// ─── Badge helpers ────────────────────────────────────────────────────────────

const LEVEL_CLASSES: Record<string, string> = {
  beginner:
    'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  intermediate:
    'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  expert:
    'border-transparent bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
};

const ORIGIN_CLASSES: Record<string, string> = {
  native:
    'border-transparent bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  meshpit:
    'border-transparent bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
};

// ─── Copy button ─────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
      onClick={handleCopy}
      title="Copy snippet"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

// ─── Live preview popover ─────────────────────────────────────────────────────

function LivePreview({ param }: { param: Parameter }) {
  const [localParam, setLocalParam] = useState<Parameter>(param);

  // No-op commit — preview only, no writeback
  const handleCommit = (_: Parameter, value: Parameter['value']) => {
    setLocalParam((prev) => ({ ...prev, value }));
  };

  // Skip types that need external resources or are non-interactive displays
  if (
    param.type === 'camera_pip' ||
    param.type === 'svgcheckboxgroup' ||
    param.type === 'svgradiogroup'
  ) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        Preview requires an SVG or 3D model file.
      </div>
    );
  }

  return (
    <div className="p-3">
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        Live Preview
      </p>
      <ParameterInput param={localParam} handleCommit={handleCommit} />
    </div>
  );
}

// ─── Main card ────────────────────────────────────────────────────────────────

type Props = {
  entry: CheatsheetEntry;
};

export function CheatsheetCard({ entry }: Props) {
  return (
    <div className="flex flex-col rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 p-4 pb-2">
        <h3 className="text-sm font-semibold leading-tight">{entry.title}</h3>
        <div className="flex shrink-0 flex-wrap gap-1">
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${LEVEL_CLASSES[entry.level]}`}
          >
            {entry.level}
          </span>
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ORIGIN_CLASSES[entry.origin]}`}
          >
            {entry.origin === 'meshpit' ? 'Meshpit' : 'Native'}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="px-4 pb-3 text-xs leading-relaxed text-muted-foreground">
        {entry.description}
      </p>

      {/* Code snippet */}
      <div className="mx-4 mb-3 rounded-md bg-muted/60 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <AnnotatedSnippet
            snippet={entry.snippet}
            annotations={entry.annotations}
          />
          <div className="flex shrink-0 items-center gap-1">
            <CopyButton text={entry.snippet} />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  title="Preview input"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="right"
                align="start"
                className="w-80 p-0"
                sideOffset={8}
              >
                <LivePreview param={entry.param} />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Footer: blog link */}
      {entry.blogSlug && (
        <div className="mt-auto border-t border-border px-4 py-2">
          <Link
            to={`/blog/${entry.blogSlug}${entry.blogAnchor ? `#${entry.blogAnchor}` : ''}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <BookOpen className="h-3 w-3" />
            {entry.blogSlug}
          </Link>
        </div>
      )}
    </div>
  );
}
